import type { MessageDto } from 'cadenza/utils/message-dto';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import { createNotification } from 'cadenza/workbook/notifications/workbook-notifications';
import type { ViewId } from 'cadenza/workbook/api/view-id';

export const RESULT_TRUNCATED_BY_ROW_COUNT = 'layer.warnings.resultTruncatedByRowCount';
export const RESULT_TRUNCATED_BY_GEOMETRY_VERTEX_COUNT = 'layer.warnings.resultTruncatedByGeometryVertexCount';
const RESULT_TRUNCATED_BY_FILL_COLOR_CLASSIFICATION_CLASSES_EXCEEDED =
  'layer.warnings.fillColorClassificationClassesExceeded';
const RESULT_TRUNCATED_BY_LINE_COLOR_CLASSIFICATION_CLASSES_EXCEEDED =
  'layer.warnings.lineColorClassificationClassesExceeded';
const RESULT_TRUNCATED_BY_OTHER_CLASSIFICATION_CLASSES_EXCEEDED =
  'layer.warnings.otherClassificationClassesExceeded';

/**
 * Codes for messages that can be set on layer sources in the map context.
 */
const SOURCE_MESSAGE_CODES = [
  RESULT_TRUNCATED_BY_ROW_COUNT,
  RESULT_TRUNCATED_BY_GEOMETRY_VERTEX_COUNT,
  RESULT_TRUNCATED_BY_FILL_COLOR_CLASSIFICATION_CLASSES_EXCEEDED,
  RESULT_TRUNCATED_BY_LINE_COLOR_CLASSIFICATION_CLASSES_EXCEEDED,
  RESULT_TRUNCATED_BY_OTHER_CLASSIFICATION_CLASSES_EXCEEDED,
  'layer.warning.unsupportedMapSrs',
  'layer.errors.authentificationFailed',
  'layer.errors.nonExisting',
  'layer.errors.initializationFailed',
  'layer.errors.data.loadingFailed'
] as const;

// this message-codes are only generated on client-side and does not exist on server-side
// (see MapFullscreenSidebarModal.#showNotifications)
const CLIENT_SIDE_SOURCE_MESSAGE_CODES = [
  'layer.warnings.resultTruncatedByRowCount.unfilterable',
  'layer.warnings.resultTruncatedByGeometryVertexCount.unfilterable',
  'layer.warnings.fillColorClassificationClassesExceeded.unfilterable',
  'layer.warnings.lineColorClassificationClassesExceeded.unfilterable',
  'layer.warnings.otherClassificationClassesExceeded.unfilterable',
  'layer.errors.data.timeout'
] as const;

type SourceMessageCode = (typeof SOURCE_MESSAGE_CODES)[number] & (typeof CLIENT_SIDE_SOURCE_MESSAGE_CODES)[number];

const VIEW_NOTIFICATION_CODES_BY_SOURCE_MESSAGE_CODE = {
  [RESULT_TRUNCATED_BY_ROW_COUNT]: 'LAYER_RESULT_TRUNCATED_BY_ROWCOUNT',
  [RESULT_TRUNCATED_BY_GEOMETRY_VERTEX_COUNT]: 'LAYER_RESULT_TRUNCATED_BY_GEOMETRY_VERTEX_COUNT',
  [RESULT_TRUNCATED_BY_FILL_COLOR_CLASSIFICATION_CLASSES_EXCEEDED]: 'RESULT_TRUNCATED_BY_FILL_COLOR_CLASSIFICATION_CLASSES_EXCEEDED',
  [RESULT_TRUNCATED_BY_LINE_COLOR_CLASSIFICATION_CLASSES_EXCEEDED]: 'RESULT_TRUNCATED_BY_LINE_COLOR_CLASSIFICATION_CLASSES_EXCEEDED',
  [RESULT_TRUNCATED_BY_OTHER_CLASSIFICATION_CLASSES_EXCEEDED]: 'RESULT_TRUNCATED_BY_OTHER_CLASSIFICATION_CLASSES_EXCEEDED',
  'layer.warnings.resultTruncatedByRowCount.unfilterable': 'LAYER_RESULT_TRUNCATED_BY_ROWCOUNT_UNFILTERABLE',
  'layer.warnings.resultTruncatedByGeometryVertexCount.unfilterable': 'LAYER_RESULT_TRUNCATED_BY_GEOMETRY_VERTEX_COUNT_UNFILTERABLE',
  'layer.warnings.fillColorClassificationClassesExceeded.unfilterable': 'RESULT_TRUNCATED_BY_FILL_COLOR_CLASSIFICATION_CLASSES_EXCEEDED',
  'layer.warnings.lineColorClassificationClassesExceeded.unfilterable': 'RESULT_TRUNCATED_BY_LINE_COLOR_CLASSIFICATION_CLASSES_EXCEEDED',
  'layer.warnings.otherClassificationClassesExceeded.unfilterable': 'RESULT_TRUNCATED_BY_OTHER_CLASSIFICATION_CLASSES_EXCEEDED',
  'layer.warning.unsupportedMapSrs': 'LAYER_UNSUPPORTED_MAP_SRS',
  'layer.errors.authentificationFailed': 'LAYER_UNAUTHENTICATED',
  'layer.errors.nonExisting': 'LAYER_NON_EXISTING',
  'layer.errors.initializationFailed': 'LAYER_INITIALIZATION_FAILED',
  'layer.errors.data.loadingFailed': 'LAYER_DATA_LOADING_FAILED',
  'layer.errors.data.timeout': 'DATA_QUERY_TIMEOUT'
} as const;

function assertSourceMessageCode (code: string): asserts code is SourceMessageCode {
  if (!SOURCE_MESSAGE_CODES.includes(code as SourceMessageCode)
    && !CLIENT_SIDE_SOURCE_MESSAGE_CODES.includes(code as SourceMessageCode)) {
    throw new Error(`Unexpected layer source message code: ${code}`);
  }
}

export function layerSourceMessageToWorkbookNotification (viewId: ViewId, layerId: LayerId, message: MessageDto) {
  // ensure it is a known code
  assertSourceMessageCode(message.code);
  return createNotification(
    message.type as 'ERROR' | 'WARNING', // the messages are filtered by type before conversion
    'VIEW',
    VIEW_NOTIFICATION_CODES_BY_SOURCE_MESSAGE_CODE[message.code],
    viewId,
    {
      layerId,
      detailParams: message.args
    }
  );
}
